import { CartButton } from '@components/CartButton';
import { CtaButton } from '@components/CtaButton';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
import PromoLabels from '@components/ProductCardNew/PromoLabels/PromoLabels';
import { FLAGS_TYPES_NAMES } from '@features/categories/const';
import { TProductFlagsKey } from '@features/categories/typescript/index';
import { TImage, TVideo } from '@components/typescript';
import theme from '@styles/theme';
import styles from './FullscreenMediaBlock.styles';
import SectionHeader from '@components/SectionHeader/SectionHeader';
import SpacingBlock from '@features/cms/components/collections/SpacingBlock/SpacingBlock';
import RenderMedia from '@utils/RenderMedia';
import Heading from '@components/Heading/Heading';
import { IFullscreenMediaBlock } from '@features/cms/typescript';
import { IProductObject } from '@features/categories/typescript';
import { useTranslation } from 'next-i18next';
export type LabelType = 'new' | 'promotion' | 'bestseller';
export type ButtonVariant = 'outline' | 'filled';
export interface FullscreenMediaBlockData {
  sectionHeader?: string | null;
  backgroundColor?: string | null;
  eyebrow?: string | null;
  title: string;
  subtitle?: string | null;
  ctaText?: string | null;
  ctaLink?: string | null;
  brandLogo?: string | null;
  labelType?: LabelType | null;
  buttonVariant?: ButtonVariant;
  buttonColor?: string | null;
  titleStyle?: 'default' | 'italic' | null;
  image?: TImage | null;
  video?: TVideo | null;
  variant?: 'centered' | 'left' | 'bottom';
  fullWidth?: boolean;
  showCartButton?: boolean;
  products?: IProductObject[] | null;
  onProductsClick?: () => void;
  marginTop?: number;
  marginBottom?: number;
  background?: string;
  code?: string;
}
export interface FullscreenMediaBlockProps {
  data: IFullscreenMediaBlock;
}
const FullscreenMediaBlock = ({
  data
}: FullscreenMediaBlockProps) => {
  const {
    sectionHeader = undefined,
    eyebrow,
    title,
    subtitle,
    ctaText,
    ctaLink,
    brandLogo,
    labelType,
    buttonVariant = 'outline',
    buttonColor,
    backgroundColor,
    image,
    video,
    variant = 'centered',
    fullWidth = false,
    showCartButton = false,
    products = [],
    marginTop,
    marginBottom,
    background,
    code
  } = data;
  const spacingBg = background || backgroundColor || theme.rawColors.neutralWhite;
  const {
    t
  } = useTranslation('common');
  const checkFlagActive = (flagName: TProductFlagsKey): boolean => {
    if (!labelType) return false;
    if (labelType === 'new') return flagName === FLAGS_TYPES_NAMES.novelty;
    if (labelType === 'promotion') return flagName === FLAGS_TYPES_NAMES.promotion;
    if (labelType === 'bestseller') return flagName === FLAGS_TYPES_NAMES.bestseller;
    return false;
  };
  const marginTopPx = Number(marginTop);
  const marginBottomPx = Number(marginBottom);
  return <>
      {Number(marginTop) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginTop)),
      code: 'mt'
    }} />}
      {sectionHeader && variant !== 'left' && <>
          <SectionHeader title={sectionHeader} sx={styles.sectionHeader} />
          <SpacingBlock data={{
        type: 'spacingBlock',
        backgroundColor: spacingBg,
        height: '40',
        code: 'spacing-1'
      }} />
        </>}
      <div sx={styles.wrapper(background || backgroundColor || undefined, fullWidth || variant === 'left')}>
        <section sx={styles.section(marginTopPx, marginBottomPx)} data-testid={code}>
          {sectionHeader && variant === 'left' && <Heading h={2} headingType={2} mobile addSx={styles.sectionHeaderTop}>
              {sectionHeader}
            </Heading>}

          {/* Background media */}
          <div sx={styles.mediaWrapper} data-testid="FullscreenMediaBlock-mediaWrapper">
            {image && RenderMedia(image, video || undefined, true, undefined, title, true)}
          </div>

          {/* Content overlay */}
          <div sx={styles.overlay} data-testid="FullscreenMediaBlock-overlay">
            <div sx={styles.content(variant)} data-testid="FullscreenMediaBlock-content">
              {/* Eyebrow - top left label */}
              {eyebrow && <span sx={styles.eyebrow}>{eyebrow}</span>}

              {/* Main content */}
              <div sx={styles.textWrapper(variant, fullWidth)} data-testid="FullscreenMediaBlock-textWrapper">
                {brandLogo && <div sx={styles.brandLogo} data-testid="FullscreenMediaBlock-brandLogo">
                    <ResponsiveImage image={brandLogo.path} alt={brandLogo.metadata?.alt || ''} width={120} height={40} objectFit="contain" />
                  </div>}
                <h2 sx={styles.title(!!eyebrow)}>{title}</h2>
                {subtitle && <p sx={styles.subtitle}>{subtitle}</p>}
                {ctaText && ctaLink && <CtaButton text={ctaText} href={ctaLink} variant={buttonVariant} color={buttonColor || undefined} />}
              </div>

              {/* Label on the side */}
              {labelType && <PromoLabels configurable={false} pricePromoPercent="" checkFlagActive={checkFlagActive} isSmall={false} transparent={false} className="promo-label" isBlock={true} />}

              {/* Cart/Products button at bottom - zawsze pokazuje sidebar z produktami, nigdy koszyk */}
              {(showCartButton || products && products.length > 0) && <CartButton image={{
              image: image as TImage
            }} video={video as TVideo} products={products as IProductObject[]} label={t('cart.viewProducts')} />}
            </div>
          </div>
        </section>
      </div>

      {Number(marginBottom) > 0 && <SpacingBlock data={{
      type: 'spacingBlock',
      backgroundColor: spacingBg,
      height: String(Number(marginBottom)),
      code: 'mb'
    }} />}
    </>;
};
export default FullscreenMediaBlock;